import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Link, To } from 'react-router-dom';

type ButtonBaseProps = {
  variant?: 'primary' | 'alternate' | 'transparent-with-border' | 'simple-text';
  className?: string;
  children: React.ReactNode;
  state?: Record<string, unknown> | null;
};

type ButtonAsButton = ButtonBaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & {
    href?: undefined;
  };

type ButtonAsAnchor = ButtonBaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & {
    href: To;
  };

type ButtonProps = ButtonAsButton | ButtonAsAnchor;

export const Button = ({
  children,
  variant = 'primary',
  className,
  href,
  state,
  ...props
}: ButtonProps) => {
  const baseStyles = `
    w-full min-h-10 px-4 flex-shrink-0 rounded-[1.75rem]
    text-center [font-feature-settings:'liga'_off,'clig'_off]
    font-ambit text-[1.0625rem] font-[900] leading-[1.5625rem]
    flex items-center justify-center cursor-pointer
    disabled:opacity-30 hover:opacity-90 md:w-auto md:px-8
  `;

  const variantStyles = {
    primary: 'bg-primary text-primary-inverse',
    alternate: 'bg-primary-inverse text-primary',
    'transparent-with-border': 'bg-transparent border border-primary text-primary',
    'simple-text': 'text-secondary underline text-base font-bold',
  };

  const commonProps = {
    className: twMerge(baseStyles, variantStyles[variant], className),
  };

  if (href) {
    return (
      <Link
        to={href}
        state={state}
        {...commonProps}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </Link>
    );
  }

  return (
    <button {...commonProps} {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
      {children}
    </button>
  );
};
